import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import Loader from './Loader';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.35);
  display: flex; justify-content: center; align-items: center;
  z-index: 999;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 600px; max-width: 96vw;
  padding: 36px 30px 28px 30px;
  box-shadow: 0 4px 20px rgba(35,62,88,0.2);
  display: flex; flex-direction: column;
`;

const ModalHeader = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 26px;
  color: #21325b;
`;

const Row = styled.div`
  display: flex; gap: 18px; margin-bottom: 20px;
`;

const HalfField = styled.div`
  flex: 1 1 0; display: flex; flex-direction: column;
`;

const FullWidthField = styled.div`
  margin-bottom: 20px;
  display: flex; flex-direction: column;
`;

const Label = styled.label`
  font-size: 1rem;
  margin-bottom: 5px;
  color: #182237;
`;

const Input = styled.input`
  padding: 10px 12px; border-radius: 8px;
  border: 1px solid #dcdcdc; font-size: 1rem;
`;

const Select = styled.select`
  padding: 10px 12px; border-radius: 8px;
  border: 1px solid #dcdcdc; font-size: 1rem;
`;

const UsernameRow = styled.div`
  display: flex; align-items: center; gap: 12px;
`;

const GenerateButton = styled.button`
  padding: 10px 16px; border-radius: 8px;
  font-weight: 700; font-size: 1rem;
  border: none; cursor: pointer; color: #fff;
  background: #426dff;
  transition: background-color 0.3s;
  &:hover { background: #305de2; }
`;

const ButtonRow = styled.div`
  display: flex; justify-content: flex-end; gap: 12px; margin-top: 32px;
`;

const Button = styled.button`
  padding: 10px 22px; border-radius: 8px;
  font-weight: 600; font-size: 1rem;
  border: none; cursor: pointer; color: #fff;
  background: ${props => props.variant === 'cancel' ? '#d24949' : '#426dff'};
  &:hover { background: ${props => props.variant === 'cancel' ? '#b33b3b' : '#305de2'}; }
`;

export default function AddUserModal({ isOpen, onClose, onUserAdded, isEdit = false, editUser = null, onUserUpdated }) {
  const [stores, setStores] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statusList,setStatusList] = useState([]);
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, storesRes, statusRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/role`,{headers}),
          axios.get(`${BACKEND_URL}/store`,{headers}),
          axios.get(`${BACKEND_URL}/user/status/all`,{headers})
        ]);
        setRoles(rolesRes.data);
        setStores(storesRes.data);
        setStatusList(statusRes.data.data);
      } catch (err) {
        console.error("Error fetching roles or stores:", err);
      }
    };
    fetchData();
  }, []);

  const [formData, setFormData] = useState({ first_name: '',last_name: '', username: '', role_id: '', contact_no: "", store_id: "",password:'',status :'' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isEdit && editUser) {
      setFormData({
        username: editUser.username || '',
        first_name: editUser.first_name|| '',
        last_name: editUser.last_name|| '',
        role_id: editUser.role?.id || '',
        password: '',
        contact_no: editUser.contact_no || '',
        store_id: editUser.store_id || editUser.store?.id || '',
        status: editUser.status
      });
    } else {
      setFormData({ first_name: '',last_name: '', username: '', role_id: '', contact_no: "", store_id: "",password:'',status:''});
      setError('');
    }
  }, [isEdit, editUser, isOpen]);

  if (!isOpen) return null;

  const isFieldDisabled = () => false;

  const handleGenerateUsername = async () => {
    if (!formData.first_name || !formData.last_name) {
      alert('Please enter first name and last name first');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await axios.post(
        `${BACKEND_URL}/auth/generate-username`,
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
        },
        { headers }
      );
      setFormData(prev => ({ ...prev, username: res.data.username }));
      const errors = validateForm({ ...formData, username: res.data.username });
      setFieldErrors(prevErrors => ({
        ...prevErrors,
        username: errors['username']
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate username');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if((e.target.name === "first_name" || e.target.name === "last_name") && !isEdit)
      setFormData(prev => ({ ...prev, username: '' }));
    setTouched(prev => ({ ...prev, [e.target.name]: true }));

    const errors = validateForm({ ...formData, [e.target.name]: e.target.value });
    setFieldErrors(prevErrors => ({
      ...prevErrors,
      [e.target.name]: errors[e.target.name]
    }));
  };

  const validateForm = (fields = formData) => {
  let errors = {};

  const nameRegex = /^[a-zA-Z\s'-]+$/; // letters, spaces, apostrophes, hyphens
  const usernameRegex = /^[a-zA-Z0-9_.-]{3,20}$/; // alphanumeric plus _ . -, length 3-20
  const contactRegex = /^[0-9]{10}$/; // 10 digit phone number
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/; // min 8 chars, at least one letter and one number

  if (!fields.first_name.trim()) {
    errors.first_name = "First name is required";
  } else if (!nameRegex.test(fields.first_name)) {
    errors.first_name = "First name contains invalid characters";
  }

  if (!fields.last_name.trim()) {
    errors.last_name = "Last name is required";
  } else if (!nameRegex.test(fields.last_name)) {
    errors.last_name = "Last name contains invalid characters";
  }

  if (!fields.role_id) {
    errors.role_id = "Role is required";
  }

  if (!fields.status) {
    errors.status = "Status is required";
  }

  // if (fields.username && !usernameRegex.test(fields.username)) {
  //   errors.username = "Username must be 3-20 chars, alphanumeric or _ . -";
  // }

  // Uncomment if password validation needed for add user (not edit)
  // if (!isEdit && !fields.password.trim()) {
  //   errors.password = "Password is required";
  // } else if (fields.password && !passwordRegex.test(fields.password)) {
  //   errors.password = "Password must be minimum 8 characters, with at least one letter and one number";
  // }

  if (fields.contact_no && !contactRegex.test(fields.contact_no)) {
    errors.contact_no = "Contact number must be 10 digits";
  }

  return errors;
};

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSubmitted(true);
    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setLoading(false);
      return;
    }
    try {
      if (isEdit && editUser) {
        const res = await axios.post(`${BACKEND_URL}/user/edit/${editUser.id}`,formData,{headers});
        if (res.status === 200) {
          alert("User updated!");
          onUserUpdated && onUserUpdated();
          onClose();
        }
      } else {
        const res = await axios.post(`${BACKEND_URL}/auth/signup`, formData,{headers});
        if (res.status === 201) {
          setFormData({ first_name: '',last_name: '', username: '', role_id: '', contact_no: "", store_id: "",password:'',status :'' });
          onUserAdded && onUserAdded();
          onClose();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      {loading && <Loader />}
      <ModalContainer>
        <ModalHeader>{isEdit ? 'Edit User' : 'Add New User'}</ModalHeader>
        {/* First + Last Name */}
        <Row>
          <HalfField>
            <Label>First Name<span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
            <Input name="first_name" value={formData.first_name} onChange={handleChange} disabled={isFieldDisabled()} />
            {(touched.first_name || submitted) && fieldErrors.first_name && <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.first_name}</span>}
          </HalfField>
          <HalfField>
            <Label>Last Name<span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
            <Input name="last_name" value={formData.last_name} onChange={handleChange} disabled={isFieldDisabled()} />
            {(touched.last_name || submitted) && fieldErrors.last_name && <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.last_name}</span>}
          </HalfField>
        </Row>
        {/* Username full width */}
        {/* <FullWidthField>
          <Label>Username<span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
          <UsernameRow>
            <Input type="text" name="username" value={formData.username} onChange={handleChange} disabled aria-label="Username" style={{ flex: 1 }} />
            {!isEdit && (
              <GenerateButton type="button" onClick={handleGenerateUsername} disabled={loading}>
                Generate Username
              </GenerateButton>
            )}
          </UsernameRow>
          {(touched.username || submitted) && fieldErrors.username && <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.username}</span>}
        </FullWidthField> */}
        {/* Role and Store */}
        <Row>
          <HalfField>
            <Label>Role<span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
            <Select name="role_id" value={formData.role_id} onChange={handleChange} disabled={isFieldDisabled()}>
              <option value="" disabled>Select Role</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.role_name}</option>
              ))}
            </Select>
            {(touched.role_id || submitted) && fieldErrors.role_id && <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.role_id}</span>}
          </HalfField>
          <HalfField>
            <Label>Store</Label>
            <Select name="store_id" value={formData.store_id} onChange={handleChange} disabled={false}>
              <option value="" >Select Store</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.friendly_name}, {store.city}, {store.state}</option>
              ))}
            </Select>
          </HalfField>
        </Row>
        {/* Contact No and Status */}
        <Row>
          <HalfField>
            <Label>Contact No.</Label>
            <Input name="contact_no" value={formData.contact_no} onChange={handleChange} disabled={isFieldDisabled()} />
                      {(touched.contact_no || submitted) && fieldErrors.contact_no && <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.contact_no}</span>}

          </HalfField>
          <HalfField>
            <Label>Status<span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
            <Select name="status" value={formData.status} onChange={handleChange} disabled={isFieldDisabled()}>
              <option value="" disabled>Select Status</option>
              {statusList.map(status => (
                <option key={status.id} value={Number(status.id)}>{status.status_code}</option>
              ))}
            </Select>
            {(touched.status || submitted) && fieldErrors.status && <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.status}</span>}
          </HalfField>
        </Row>
        {/* Password (add only) */}
        {/* {!isEdit && (
          <FullWidthField>
            <Label>Password<span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
            <Input type="password" name="password" value={formData.password} onChange={handleChange} />
            {(touched.password || submitted) && fieldErrors.password && <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.password}</span>}
          </FullWidthField>
        )} */}
        {error && <div style={{ color: '#d24949', marginBottom: 10 }}>{error}</div>}
        <Label style={{marginTop:'36px'}}><span style={{ color: 'red', marginLeft: '4px' }}>*</span> marked fields are required.</Label>
        <ButtonRow>
          <Button variant="cancel" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update User' : 'Add User')}
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}
